import React, { useEffect, useState, FC } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import * as types from "../gql/graphql";

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import AddBoxIcon from '@mui/icons-material/AddBox';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Checkbox from "@mui/material/Checkbox";
import MenuItem from '@mui/material/MenuItem';

import MDBox from './MDBox/MDBoxRoot';
import setThemeObj from './../hooks/useThemeMUI';
import MDTypography from './MDTypography/MDTypographyRoot';
import MDButton from './MDButton/MDButtonRoot';
import MDInput from './MDInput/MDInputRoot';

import { SubmitErrorHandler, SubmitHandler, useForm, Controller } from 'react-hook-form';
import { useQueryAll } from '../apolloClient';

interface EditClassDialogProps {
    rowData: any; // Adjust the type according to your data structure
    //render: (open: any) => any;
    onSave: () => void;
    onClose: () => void;
}

const getEventTypesQuery = gql(`query GetEventTypes {
    fs_eventtypeCollection {
        edges {
          node {
            nodeId
            id
            typename
          }
        }
      }
  }`);

const getProductCategoriesQuery = gql(`query GetProductCategories {
    fs_prodcategoryCollection {
        edges {
          node {
            nodeId
            id
            catname
          }
        }
      }
  }`);

const getClassesForDependenciesQuery = gql(`
  query GetClassesForDependencies($after: Cursor) {
    fs_classesCollection(after: $after) {
        pageInfo { endCursor startCursor hasNextPage } 
        edges {
          node {
            nodeId
            id
            name
          }
        }
      }
  }
`);


type ClassItem = {
    className: string;
    classId: number;
};

function CompareListsOfID(oldList: ClassItem[], newList: ClassItem[]) {
    const addClasses: ClassItem[] = [];
    const deleteIDs: number[] = [];

    // Find additions by checking which class IDs are in newList but not in oldList
    newList.forEach(obj => {
        if (!oldList.find(oldObj => oldObj.classId === obj.classId))
            addClasses.push(obj); // Add class with changed repetitions or new class
    }
    );

    // Find deletions by checking which class IDs are in oldList but not in newList
    oldList.forEach(oldObj => {
        if (!newList.find(obj => oldObj.classId === obj.classId))
            deleteIDs.push(oldObj.classId); // Add class with changed repetitions or new class
    }
    );

    return { addClasses, deleteIDs };
}

function Form(rowData: any, onSave, onClose) {

    const { data: ETdata } = useQuery(getEventTypesQuery);
    const { data: PCdata } = useQuery(getProductCategoriesQuery);
    const { data: Cdata } = useQueryAll<types.Fs_ClassesEdge>({ query: getClassesForDependenciesQuery });
    const [selectedClassID, setSelectedClassID] = useState(null);
    const [selectedClassValue, setSelectedClassValue] = useState(null);
    const [dependenciesList, setDependenciesList] = useState<ClassItem[]>(null);

    //console.log(PCdata);
    //console.log("data");
    rowData = rowData || { listorder: 1000, id: -1, eventtype: 0, category: 0, active: true };

    const { register,
        control,
        handleSubmit,
        clearErrors,
        setValue,
        reset,
        watch,
        formState: { errors } } = useForm<types.Fs_ClassesUpdateInput>({
            mode: 'onChange',
            defaultValues: rowData
        })

    const saveClassDataQuery = gql(`
        mutation saveClassDataQuery($cl: fs_classesUpdateInput!, $filter:fs_classesFilter) {
            updatefs_classesCollection(set: $cl, filter: $filter) {
                affectedCount
                records {
                    name
                }
            }
        }      
        `);

    const [saveClassMutation, { data, loading, error }] = useMutation(saveClassDataQuery, {
        refetchQueries: [
            'GetClasses' // Query name
        ],
    });

    const addClassDataQuery = gql(`
    mutation addClassDataQuery($cl: fs_classesInsertInput!) {
        insertIntofs_classesCollection(objects: [$cl]) {
            affectedCount
        }
    }      
    `);

    const [addClassMutation, { data: add_data, loading: add_loading, error: add_error }] = useMutation(addClassDataQuery, {
        refetchQueries: [
            'GetClasses' // Query name
        ],
    });


    // Dependencies handlers ////////////////////////
    const getDependenciesForClassQuery = gql(`
        query GetDependenciesForClass($filter: fs_classdependenciesFilter) {
        fs_classdependenciesCollection(filter: $filter) {
            edges {
            node {
                nodeId
                depends_on
                fs_classes {
                nodeId
                id
                name
                }
            }
            }
        }
        }
        `);
    const addDependenciesQuery = gql(`
            mutation addClassDependencies($deps: [fs_classdependenciesInsertInput!]! ) {
                insertIntofs_classdependenciesCollection(objects:$deps) {
                    affectedCount
                }
            }  
        `);

    const [addDependenciesMutation, { called: addCalled, loading: addLoading }] = useMutation(addDependenciesQuery, {
        refetchQueries: [
            'GetDependenciesForClass' // Query name
        ], awaitRefetchQueries: true
    });

    const deleteDependenciesQuery = gql(`
            mutation deleteClassesDependenciesQuery($filter:fs_classdependenciesFilter) {
                deleteFromfs_classdependenciesCollection(filter: $filter, atMost: 20) {
                    affectedCount
                }
            }
        `);

    const [deleteDependenciesMutation, { called: delCalled, loading: delLoading }] = useMutation(deleteDependenciesQuery, {
        refetchQueries: [
            'GetDependenciesForClass' // Query name
        ], awaitRefetchQueries: true
    });

    const { data: DepCdata } = useQuery(getDependenciesForClassQuery, { variables: { filter: { class_id: { eq: rowData.id } } } });
    // { "filter": { "class_id": { "eq": 173 }}}

    // Initilize list of classes
    useEffect(() => {
        if (DepCdata) {
            const classes: ClassItem[] = [];
            DepCdata.fs_classdependenciesCollection.edges.map(r => {
                classes.push({ className: r.node.fs_classes.name, classId: r.node.fs_classes.id });
            });
            setDependenciesList(classes);
        }
    }, [DepCdata]);


    const handleSelectChange = (event) => {
        console.log('event.target.value', event.target.value);
        setSelectedClassID(event.target.value);
        let selectedName = "";
        for (const item of Cdata) {
            if (item.node.id === event.target.value) {
                selectedName = item.node.name;
                break;
            }
        }
        const newSelectedOption = { id: event.target.value, name: selectedName };
        setSelectedClassValue(newSelectedOption);
    };

    const handleAddValue = () => {
        if (selectedClassID) {
            const isDuplicate = dependenciesList.findIndex(option => option.classId == selectedClassID) != -1;
            if (!isDuplicate) {
                setDependenciesList([...dependenciesList, { classId: selectedClassValue.id, className: selectedClassValue.name }]);
            }
        }
    };

    const handleRemoveValue = (index) => {
        const updatedOptions = [...dependenciesList];
        updatedOptions.splice(index, 1);
        setDependenciesList(updatedOptions);
    };


    /////////////////////////////////////////////////



    const submit: SubmitHandler<types.Fs_ClassesUpdateInput> = async data => {

        // TODO: Data has all row fields, which is more than is defined in the input for saving - types.Fs_ClassesUpdateInput
        // Now filtering extra fields here. Need to find a better way to do that. 
        // Will using state management library help? Redux?
        function pickObjectKeys<T, K extends keyof T>(obj: T, keys: K[]) {
            let result = {} as Pick<T, K>
            for (const key of keys) {
                if (obj.hasOwnProperty(key)) {
                    result[key] = obj[key]
                }
            }
            return result
        }
        // TODO: Check duplicate code or name
        // TODO: provide default value for listorder. Where?
        const filtered_data = pickObjectKeys(data, ['active', 'cancelcutoff', 'code', 'description', 'followupmsg',
            'listorder', 'duration', 'name', 'notificationmsg', 'signcutoff', 'signupmsg', 'wlcutoff', 'eventtype', 'category', 'multi']);

        if (rowData.id == -1) {
            addClassMutation({ variables: { cl: filtered_data } });
        }
        else {
            saveClassMutation({ variables: { filter: { nodeId: { eq: rowData.nodeId } }, cl: filtered_data } });

            // Save dependencies if changed
            const origDeps: ClassItem[] = [];
            if (DepCdata) {
                DepCdata.fs_classdependenciesCollection.edges.map(r => {
                    origDeps.push({ className: r.node.fs_classes.name, classId: r.node.fs_classes.id });
                });
            }
            const changes = CompareListsOfID(origDeps, dependenciesList);

            if (changes.deleteIDs.length) {
                await deleteDependenciesMutation({ variables: { filter: { and: { depends_on: { in: changes.deleteIDs }, class_id: { eq: rowData.id } } } } });
            }

            if (changes.addClasses.length) {
                const additions = [];
                changes.addClasses.map(additionalClass => { additions.push({ depends_on: additionalClass.classId, class_id: rowData.id }) });
                await addDependenciesMutation({ variables: { deps: additions } });
            }
        }
        onSave();
    }

    const errorHandler: SubmitErrorHandler<types.Fs_ClassesUpdateInput> = data => {
    }

    // Message help dialog state
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpFieldName, setHelpFieldName] = useState<string | null>(null);

    const openHelp = (fieldName: string) => {
        setHelpFieldName(fieldName);
        setHelpOpen(true);
    };

    //console.log('watch', watch);
    return (
        <MDBox theme={setThemeObj}
            component="main" display="flex"
            justifyContent="space-between"
            alignItems="baseline"
            flexDirection="column"
            pt={2}
            pb={0.5}
            px={2}
            sx={{ flexGrow: 1, width: "100%" }}
            width="100%"
        >
            <form onSubmit={handleSubmit(submit, errorHandler)} id="testForm">
                <MDBox component="table">
                    <MDBox component="tbody">
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Name</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">{/*component="form" role="form" */}
                                <MDInput
                                    {...register('name', { required: true })}
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                />
                                {errors.name && (
                                    <MDBox>
                                        <MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>
                                            This field is required
                                        </MDTypography>
                                    </MDBox>
                                )}                            
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Description</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('description')}
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Active?</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <Controller
                                    name="active"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Checkbox checked={value} onChange={onChange} />
                                    )}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Category</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    id="standard-select-currency"
                                    {...register('category', { valueAsNumber: true, validate: value => value !== 0 || "This field is required" })}
                                    select
                                    defaultValue={rowData?.category || "0"}
                                    variant="standard"
                                    fullWidth
                                >
                                    <MenuItem value="0">Select Category</MenuItem>
                                    {PCdata?.fs_prodcategoryCollection.edges.map((item) => (
                                        <MenuItem key={item.node.id} value={item.node.id}>
                                            {item.node.catname}
                                        </MenuItem>
                                    ))}
                                </MDInput>
                                {errors.category && (
                                    <MDBox>
                                        <MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>
                                            This field is required
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Type</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    id="standard-select-currency"
                                    {...register('eventtype', { valueAsNumber: true, validate: value => value !== 0 || "This field is required" })}
                                    defaultValue={rowData?.eventtype || "0"}
                                    select
                                    variant="standard"
                                    fullWidth
                                >
                                    <MenuItem value="0">Select an event type</MenuItem>
                                    {ETdata?.fs_eventtypeCollection.edges.map((item) => (
                                        <MenuItem key={item.node.id} value={item.node.id}>
                                            {item.node.typename}
                                        </MenuItem>
                                    ))}
                                </MDInput>
                                {errors.eventtype && (
                                    <MDBox>
                                        <MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>
                                            This field is required
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Notification Message</MDTypography>
                                <HelpOutlineIcon fontSize="small" style={{ cursor: 'pointer' }} onClick={() => openHelp('notificationmsg')} />
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('notificationmsg')}
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Follow-up Message</MDTypography>
                                <HelpOutlineIcon fontSize="small" style={{ cursor: 'pointer' }} onClick={() => openHelp('followupmsg')} />
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('followupmsg')}
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Sign-up Message</MDTypography>
                                <HelpOutlineIcon fontSize="small" style={{ cursor: 'pointer' }} onClick={() => openHelp('signupmsg')} />
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('signupmsg')}
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Duration</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('duration', { valueAsNumber: true, required: true, minLength: 3 })}
                                    type='number'
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.duration ? true : false}
                                />
                                {errors.duration && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Multiple credits in package</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <Controller
                                    name="multi"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Checkbox checked={value} onChange={onChange} />
                                    )}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Code</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('code', { required: true })}
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.code ? true : false}
                                />
                                {errors.code && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Sign-up Cutoff</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('signcutoff', { valueAsNumber: true, required: true })}
                                    type='number'
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.signcutoff ? true : false}
                                />
                                {errors.signcutoff && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Cancellation Cuttoff</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('cancelcutoff', { valueAsNumber: true, required: true })}
                                    type='number'
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.cancelcutoff ? true : false}
                                />
                                {errors.cancelcutoff && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Waitlist Cuttoff</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('wlcutoff', { valueAsNumber: true, required: true })}
                                    type='number'
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.wlcutoff ? true : false}
                                />
                                {errors.wlcutoff && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Order in schedule</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('listorder', { valueAsNumber: true, required: true })}
                                    type='number'
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.listorder ? true : false}
                                />
                                {errors.listorder && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                        </MDBox>

                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Depends on</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    //{...register('classes', { valueAsNumber: true })}
                                    onChange={handleSelectChange}
                                    value={selectedClassID ? selectedClassID : 0}
                                    select
                                    variant="standard"
                                    fullWidth>
                                    <MenuItem value="0">Select Classes</MenuItem>
                                    {Cdata?.map((item) => { return <MenuItem value={item.node.id} key={item.node.id}>{item.node.name}</MenuItem> })}
                                </MDInput>
                            </MDBox>
                            <MDBox px={1} component="td">
                                <MDButton
                                    startIcon={<AddBoxIcon />}
                                    size="large"
                                    ownerState={{ color: "info", iconOnly: true }}
                                    onClick={handleAddValue} />
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td"></MDBox>
                            <MDBox px={1} component="td">
                                <MDBox component="table">
                                    <MDBox component="tbody">
                                        {dependenciesList?.map((option, index) => (<>
                                            <MDBox px={1} key={index} component="tr">
                                                <MDBox px={1} component="td" sx={{ width: "70%" }}>
                                                    <MDTypography variant="h5" px={1} ownerState={{ color: "dark" }}>{option.className}
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox px={1} component="td">
                                                    <MDButton
                                                        startIcon={<HighlightOffIcon />}
                                                        size="large"
                                                        name='newclassInput'
                                                        ownerState={{ color: "error", iconOnly: true }}
                                                        onClick={() => handleRemoveValue(index)} aria-label="add" />
                                                </MDBox>
                                            </MDBox>
                                        </>
                                        ))}
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </MDBox>


                    </MDBox> {/*Tbody*/}
                </MDBox> {/*Table*/}

                <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="baseline"
                    flexDirection="row"
                    gap="16px"
                    pt={2}
                    pb={0.5}
                    px={2}
                    width="100%"
                >
                    <MDButton
                        ownerState={{
                            variant: "gradient",
                            color: "info",
                            textTransform: "none",
                            circular: false,
                            iconOnly: false,
                            darkMode: false
                        }}
                        size="medium"
                        type="submit">
                        Submit
                    </MDButton>
                    <MDButton
                        size="medium"
                        onClick={() => onClose()}
                        ownerState={{
                            textTransform: "none",
                            variant: "gradient",
                            color: "warning",
                        }}>
                        Cancel
                    </MDButton>
                </MDBox>

            </form>

            <MessageFormatHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} field={helpFieldName} />

        </MDBox>
    )
}


const EditClassDialog: FC<EditClassDialogProps> = ({ rowData, onSave, onClose }) => {
    // Your modal content and form for editing the row data
    // Is it needed?
    const handleEntered = () => {
        // The `autoFocus` is not used because, if used, the same Enter that saves
        // the cell triggers "No". Instead, we manually focus the "No" button once
        // the dialog is fully open.
        // noButtonRef.current?.focus();
    };

    // Do we need dialog?
    return (
        <Dialog
            maxWidth="sm"
            TransitionProps={{ onEntered: handleEntered }}
            open={true}
            fullWidth
        >
            <MDTypography variant="h5" px={1} py={1}>Save class? <CloseIcon onClick={onClose} sx={{ position: "absolute", top: "10px", right: "10px" }} /></MDTypography>
            <DialogContent dividers>
                {Form(rowData, onSave, onClose)}
            </DialogContent>
        </Dialog>
    );
};
        
// Help dialog rendered once per form
const MessageFormatHelpDialog = ({ open, onClose, field }: { open: boolean; onClose: () => void; field: string | null }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
            <MDTypography variant="h5">Message Format</MDTypography>
            <MDTypography variant="body1" sx={{ mt: 2 }}>
                Use the following format for messages (Notification, Follow-up, Sign-up):
            </MDTypography>
            <MDBox component="ol" sx={{ pl: 3 }}>
                <li><MDTypography variant="body2">First line — subject line for the email</MDTypography></li>
                <li><MDTypography variant="body2">Remaining lines — the body of the email</MDTypography></li>
            </MDBox>
            <MDTypography variant="body2" sx={{ mt: 2 }}>
                Example:
            </MDTypography>
            <MDBox sx={{ background: '#f6f6f6', p: 2, mt: 1 }}>
                <pre>{`Welcome to class
Hey!
Thanks for joining our class.`}</pre>
            </MDBox>
        </DialogContent>
        <MDBox sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <MDButton onClick={onClose}>Close</MDButton>
        </MDBox>
    </Dialog>
);


export default EditClassDialog;
